import React, { memo, useContext, useMemo } from 'react';

import { useRecoilValue } from 'recoil';

import {
  blockExportMetadata,
  blockInfoGraphicType,
  blockMetrics,
  blockSettings,
  customizedBlock,
  isReportState,
  viewDataGridSizeType,
} from 'venn-state';

import { useFilteredRequestsAndAnalyses } from '../../utils/hooks/use-filtered-requests';

import { useAppPrintMode } from '../../../print';
import {
  DATA_TYPE_EXCEL_STYLES,
  DataGridSizeType,
  useResponseParser,
  type AnalysisRequest,
  type ExportMetaData,
} from '../../../studio-blocks';
import useAnalysisGrid from '../../utils/hooks/use-analysis-grid';

import { getAnalyzedSubjectFromRequestSubject } from 'venn-utils';
import { useSubjectColors } from '../../../studio-blocks/logic/useSubjectColors';
import type { AnalysisProps } from '../../types';

import { ThemeContext } from 'styled-components';
import type { Analysis } from 'venn-api';
import { GridWrapper } from '../../../studio-blocks/common';
import { FactorBar } from '../../../studio-blocks/components/charts/factor-bar';
import { analysisGridDefaultColDefOverrides } from '../../utils';
import ExportableGrid from '../../../studio-blocks/components/grid/ExportableGrid';
import { useCommonGridProps } from '../../../studio-blocks/logic/columnUtils';
import { pivotGridData } from '../../../studio-blocks/logic/pivotGridData';
import { isReadyToPivotData } from '../../../studio-blocks/logic/typeUtils';
import { useSelectedMetrics } from '../../../studio-blocks/logic/useSelectedMetrics';

interface FactorContributionContentProps {
  id: string;
}

export const FactorContributionContent = ({ id }: FactorContributionContentProps) => {
  const { inPrintModeOrReportLab } = useAppPrintMode();

  const { requests, analyses } = useFilteredRequestsAndAnalyses(id);

  const isReport = useRecoilValue(isReportState);
  const infoGraphicType = useRecoilValue(blockInfoGraphicType(id));

  const isCompact = useRecoilValue(viewDataGridSizeType(id)) === DataGridSizeType.COMPACT;
  const exportMetaData = useRecoilValue(blockExportMetadata(id));

  return infoGraphicType === 'GRID' || infoGraphicType === 'PIVOTED_GRID' ? (
    <Grid
      id={id}
      requests={requests}
      analyses={analyses}
      inPrintMode={inPrintModeOrReportLab}
      isCompact={isCompact}
      exportMetaData={exportMetaData}
      isReport={isReport}
      pivoted={infoGraphicType === 'PIVOTED_GRID'}
    />
  ) : infoGraphicType === 'DIVERGENT_BAR' ? (
    <Chart id={id} requests={requests} analyses={analyses} exportMetaData={exportMetaData} />
  ) : null;
};

type GridProps = AnalysisProps & { pivoted: boolean | undefined };

const Grid = memo(function InternalGrid({
  id,
  requests,
  analyses: analysesGroup,
  isCompact = false,
  isReport,
  pivoted,
  ...otherProps
}: GridProps) {
  const { Colors } = useContext(ThemeContext);

  const selectedBlock = useRecoilValue(customizedBlock(id));
  const { customBlockType } = useRecoilValue(blockSettings(id));
  const selectedMetricIds = useRecoilValue(blockMetrics(id));
  const responseParser = useResponseParser(id);

  const analysisGridData = useAnalysisGrid(
    requests,
    responseParser,
    Colors,
    analysesGroup,
    selectedBlock,
    customBlockType,
    selectedMetricIds,
  );

  const { rowData, columnDefs } = useMemo(
    () =>
      pivoted && isReadyToPivotData(analysisGridData.rowData)
        ? pivotGridData(analysisGridData.rowData, analysisGridData.columnDefs)
        : { rowData: analysisGridData.rowData, columnDefs: analysisGridData.columnDefs },
    [analysisGridData.columnDefs, analysisGridData.rowData, pivoted],
  );

  const commonGridProps = useCommonGridProps(analysisGridDefaultColDefOverrides);

  return (
    <GridWrapper isReport={isReport}>
      <ExportableGrid
        {...otherProps}
        {...commonGridProps}
        selectedRefId={id}
        exportable
        rowData={rowData}
        columnDefs={columnDefs}
        excelStyles={DATA_TYPE_EXCEL_STYLES}
        isCompact={isCompact}
      />
    </GridWrapper>
  );
});

export interface ChartProps {
  id: string;
  requests: AnalysisRequest[];
  analyses: (Analysis | undefined)[][];
  exportMetaData: ExportMetaData;
}

const Chart = memo(function InternalChart({ id, requests, analyses, exportMetaData }: ChartProps) {
  const analyzedSubjects = useMemo(
    () => requests.map(({ subject }) => getAnalyzedSubjectFromRequestSubject(subject)),
    [requests],
  );
  const subjectColors = useSubjectColors(analyzedSubjects);
  const metrics = useSelectedMetrics();
  const selectedBlock = useRecoilValue(customizedBlock(id));
  const responseParser = useResponseParser(id);

  return (
    <FactorBar
      selectedRefId={id}
      requests={requests}
      analyses={analyses}
      selectedBlock={selectedBlock}
      exportMetaData={exportMetaData}
      subjectColors={subjectColors}
      metrics={metrics}
      responseParser={responseParser}
    />
  );
});
